/**
 * Taking EXIF out of a JPEG without touching the picture.
 *
 * The canvas is the easy way to strip metadata — draw the pixels, keep
 * nothing else — but it also throws away everything the camera did. A Pixel
 * shoots Ultra HDR: the file holds an ordinary image plus a gain map that
 * says how much brighter each part should be on a screen that can show it.
 * A 2D canvas cannot hold that, so what came back was the flat base of a
 * photograph its owner had just previewed in HDR, converted from Display P3
 * to sRGB on the way. That is the "terrible lighting".
 *
 * So the bytes are left exactly as the camera wrote them and only the EXIF is
 * emptied — and emptied *in place*, keeping the segment's length, because the
 * gain map is reached by a byte offset recorded earlier in the file. Removing
 * the segment outright would shorten everything after it and leave that offset
 * pointing at the wrong place, which loses the gain map by a different route.
 */

/** Big enough for any phone's photograph, small enough to refuse a scan. */
const MAX_PASS_THROUGH_BYTES = 12e6;

export interface KeptJpeg {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * The file as it was, minus its EXIF, or null when that cannot be done
 * safely — in which case the caller falls back to the canvas, which is slower
 * and worse-looking but certain.
 */
export async function withoutExif(file: File): Promise<KeptJpeg | null> {
  if (file.type !== 'image/jpeg' || file.size > MAX_PASS_THROUGH_BYTES) return null;

  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

  let at = 2;
  let width = 0;
  let height = 0;
  let turn = 1;

  while (at < bytes.length - 1) {
    if (bytes[at] !== 0xff) return null;

    const marker = bytes[at + 1];
    // Padding between segments is legal, and standalone markers carry no
    // length to skip by.
    if (marker === 0xff) {
      at += 1;
      continue;
    }
    // Start of scan: the compressed picture follows and there is no more
    // metadata to find.
    if (marker === 0xda || marker === 0xd9) break;

    const length = (bytes[at + 2] << 8) | bytes[at + 3];
    if (length < 2 || at + 2 + length > bytes.length) return null;

    const from = at + 4;
    const size = length - 2;

    if (marker === 0xe1) {
      if (startsWith(bytes, from, 'Exif\0\0')) {
        turn = blankExif(bytes, from, size);
      } else if (isXmpWithLocation(bytes, from, size)) {
        /*
         * XMP is where the gain map describes itself, so it cannot simply be
         * emptied — but it is also somewhere a phone may write coordinates.
         * When it holds both, privacy wins and the caller redraws it.
         */
        return null;
      }
    }

    // The frame header, which is where the size of the picture is written.
    // Read rather than decoded: nothing here needs the pixels.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      height = (bytes[from + 1] << 8) | bytes[from + 2];
      width = (bytes[from + 3] << 8) | bytes[from + 4];
    }

    at += 2 + length;
  }

  if (!width || !height) return null;

  /*
   * The frame header describes the sensor, which on a phone held upright is
   * lying on its side. Everything that lays the picture out wants the shape it
   * will be seen at, so the quarter turns report the other way round.
   */
  const turned = turn >= 5 && turn <= 8;
  return {
    blob: new Blob([bytes], { type: 'image/jpeg' }),
    width: turned ? height : width,
    height: turned ? width : height,
  };
}

/**
 * An EXIF block holding one tag and nothing else, padded out to the length the
 * old one had so that every byte after it stays where it was.
 *
 * The tag it keeps is Orientation, and leaving it out is a mistake worth
 * recording: a phone does not turn the picture, it writes down which way up
 * the camera was and lets the viewer turn it. The canvas path did the turning
 * itself, so throwing the whole block away cost nothing there. Handing over
 * the camera's own bytes, it cost everything — the first selfie through this
 * came out on its side.
 *
 * Returns the value it carried across, because the frame header measures the
 * sensor and the caller needs to know whether that is the shape anyone will
 * actually see.
 */
function blankExif(bytes: Uint8Array, from: number, size: number): number {
  const turn = readOrientation(bytes, from, size) ?? 1;
  const kept = [
    0x45, 0x78, 0x69, 0x66, 0x00, 0x00, // "Exif\0\0"
    0x49, 0x49, 0x2a, 0x00, // little-endian TIFF
    0x08, 0x00, 0x00, 0x00, // first directory is at byte eight
    0x01, 0x00, // holding one entry
    0x12, 0x01, // tag 0x0112, Orientation
    0x03, 0x00, // as a short
    0x01, 0x00, 0x00, 0x00, // one of them
    turn & 0xff, (turn >> 8) & 0xff, 0x00, 0x00, // which way up
    0x00, 0x00, 0x00, 0x00, // and no directory after this one
  ];
  if (size < kept.length) return 1;
  bytes.fill(0, from, from + size);
  bytes.set(kept, from);
  return turn;
}

/**
 * Which way up the camera was, read out of the block before it is replaced.
 *
 * Only the first directory is walked: Orientation lives there, and the point
 * is to carry one number across rather than to understand the file.
 */
function readOrientation(bytes: Uint8Array, from: number, size: number): number | null {
  const tiff = from + 6;
  if (size < 14) return null;

  const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
  if (!little && !(bytes[tiff] === 0x4d && bytes[tiff + 1] === 0x4d)) return null;

  const short = (at: number): number =>
    little ? bytes[at] | (bytes[at + 1] << 8) : (bytes[at] << 8) | bytes[at + 1];
  const long = (at: number): number =>
    little
      ? bytes[at] | (bytes[at + 1] << 8) | (bytes[at + 2] << 16) | (bytes[at + 3] << 24)
      : (bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3];

  if (short(tiff + 2) !== 42) return null;

  const ifd = tiff + long(tiff + 4);
  if (ifd + 2 > from + size) return null;

  const entries = short(ifd);
  for (let i = 0; i < entries; i += 1) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > from + size) return null;
    if (short(entry) !== 0x0112) continue;
    const value = short(entry + 8);
    return value >= 1 && value <= 8 ? value : null;
  }
  return null;
}

function startsWith(bytes: Uint8Array, from: number, text: string): boolean {
  for (let i = 0; i < text.length; i += 1) {
    if (bytes[from + i] !== text.charCodeAt(i)) return false;
  }
  return true;
}

/** Whether an XMP packet mentions where the photograph was taken. */
function isXmpWithLocation(bytes: Uint8Array, from: number, size: number): boolean {
  if (!startsWith(bytes, from, 'http://ns.adobe.com/xap/1.0/\0')) return false;
  const text = new TextDecoder().decode(bytes.subarray(from, from + size));
  return /GPS(Latitude|Longitude|Altitude)/i.test(text);
}
